import styled from 'styled-components';

export const AssetsContainer = styled.div`
  padding: 2rem;
  background: var(--surface-glass);
`;

export const AssetsTitle = styled.h2`
  text-align: center;
  color: var(--text-heading);
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
`;

export const AssetsSubtitle = styled.p`
  text-align: center;
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

export const AssetsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding: 0 1rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
    padding: 0 0.5rem;
  }
`;

export const AssetCard = styled.div`
  background: var(--surface-1);
  border-radius: 15px;
  padding: 1.5rem;
  box-shadow: var(--shadow-sm);
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-md);
    border-color: var(--accent);
  }
  
  /* Faixa de marca no topo do card (verde/azul Vert). */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--brand-cta-gradient);
  }

  @media (max-width: 768px) {
    padding: 1.2rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

export const AssetHeader = styled.div`
  margin-bottom: 1rem;

  @media (max-width: 480px) {
    margin-bottom: 0.8rem;
  }
`;

export const AssetName = styled.h3`
  color: var(--text-primary);
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0.5rem;
  border-radius: 8px;
  margin: -0.5rem -0.5rem 0.5rem -0.5rem;

  &:hover {
    color: var(--accent);
    background: var(--accent-soft);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-bottom: 0.4rem;
  }
`;

/* Badge sobre gradiente de marca: texto branco fixo para contraste. */
export const AssetType = styled.span`
  background: var(--brand-cta-gradient);
  color: var(--text-on-brand);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-block;

  @media (max-width: 480px) {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
  }
`;

export const AssetDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

export const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-subtle);

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.3rem;
    padding: 0.4rem 0;
  }
`;

export const DetailLabel = styled.span`
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 0.9rem;
  min-width: 120px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    min-width: auto;
    font-size: 0.85rem;
  }
`;

export const DetailValue = styled.span`
  color: var(--text-primary);
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  line-height: 1.4;

  @media (max-width: 480px) {
    text-align: left;
    font-size: 0.85rem;
  }
`;

export const AssetFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 2px solid var(--border-default);

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.8rem;
    align-items: stretch;
    padding-top: 0.8rem;
  }
`;

export const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.75rem;
  margin-top: 1rem;
  width: 100%;

  & > a,
  & > button {
    width: 100%;
    min-width: 0;
    min-height: 2.5rem;
    padding: 0.5rem 0.5rem;
    font-size: clamp(0.65rem, 0.85vw, 0.75rem);
    line-height: 1.15;
    text-align: center;
    white-space: normal;
    word-break: break-word;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const cardButtonShared = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  font-family: inherit;
`;

export const CardButtonOutline = styled.button`
  ${cardButtonShared}
  border: 2px solid var(--accent);
  background: transparent;
  color: var(--accent);

  &:hover {
    background: var(--accent-soft);
    transform: translateY(-1px);
  }
`;

/* Botão principal sobre gradiente de marca: texto branco fixo. */
export const CardButtonSolid = styled.button`
  ${cardButtonShared}
  border: none;
  color: var(--text-on-brand);
  background: var(--brand-cta-gradient);
  box-shadow: var(--shadow-sm);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
`;

/* Destaque de quantidade sobre gradiente de marca: texto branco fixo. */
export const QuantityBadge = styled.div`
  background: var(--brand-cta-gradient);
  color: var(--text-on-brand);
  padding: 0.6rem 1rem;
  border-radius: 25px;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: var(--shadow-sm);

  @media (max-width: 480px) {
    padding: 0.5rem 0.8rem;
    font-size: 0.85rem;
  }
`;

export const StandardBadge = styled.div`
  background: var(--surface-3);
  color: var(--text-primary);
  padding: 0.6rem 1rem;
  border-radius: 25px;
  font-weight: 500;
  font-size: 0.85rem;
  border: 1px solid var(--border-subtle);

  @media (max-width: 480px) {
    padding: 0.5rem 0.8rem;
    font-size: 0.8rem;
    text-align: center;
  }
`;

export const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
`;

export const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid var(--accent-soft);
  border-top: 4px solid var(--accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const ErrorMessage = styled.div`
  background: var(--surface-2);
  color: var(--danger);
  padding: 2rem;
  border-radius: 10px;
  text-align: center;
  border: 1px solid var(--danger);
  font-size: 1.1rem;
`;

export const NoResultsMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
  font-size: 1.1rem;
  background: var(--surface-glass);
  border-radius: 10px;
  margin: 1rem 0;
`;
